import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import type { ValidationError } from "~/common/types";
import {
  GetTenantDocument,
  GetTenantsDocument,
  UpdateTenantDocument,
  type UpdateTenantMutation,
  type UpdateTenantMutationVariables,
} from "~/features/admin/tenants/gqls";
import type { TenantEditFormData } from "~/features/admin/tenants/types";
import { translateError } from "~/lib/errors";

interface UseTenantEditOptions {
  onSuccess?: (data: TenantEditFormData) => void;
  onError?: (error: Pick<ValidationError, "fields">) => void;
}

/** Update a tenant's name and auto-approve flag. The shortCode is immutable. */
export function useTenantEdit({ onSuccess, onError }: UseTenantEditOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [updateTenant] = useMutation<UpdateTenantMutation, UpdateTenantMutationVariables>(UpdateTenantDocument, {
    refetchQueries: [GetTenantDocument, GetTenantsDocument],
  });

  const submit = useCallback(
    async (formData: TenantEditFormData) => {
      setProcessing(true);
      try {
        const res = await updateTenant({
          variables: {
            input: {
              id: formData.id,
              name: formData.name,
              allowAutoApproveChangeRequests: formData.allowAutoApproveChangeRequests,
            },
          },
        });
        match(res.data?.cnutUpdateTenant)
          .with({ __typename: "ValidationError" }, (e) => onError?.(e))
          .with({ __typename: "TenantUpdateOutput" }, () => {
            toast.success(t("feature.common.updatedSuccessfully", { model: "tenant" }));
            onSuccess?.(formData);
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [updateTenant, t, onSuccess, onError],
  );

  return { submit, processing };
}
